import React, { useState, useEffect } from 'react';
import Link from "next/link";
import { DrupalNode } from "next-drupal";
import { absoluteUrl, formatDate } from "lib/utils";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { AudioPlayerButton } from "@/components/audioPlayer";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import SliderIndicator from '../ui/slider-indicator';
import ImageWithFallback from '../ui/image';

interface NodePodcastPromotedProps {
  podcasts: DrupalNode[]
  title?: string
}

export function NodePodcastPromotedSlider({ podcasts, title, ...props }: NodePodcastPromotedProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      if (!api) return;
      setIsStart(!api.canScrollPrev());
      setIsEnd(!api.canScrollNext());
    });
  }, [api]);

  return (
    <Carousel
      setApi={setApi}
      className=''
      opts={{
        align: "start",
        dragFree: true,
      }}
    >
      <div className="w-full overflow-hidden">
        {!isStart &&
          <div className="h-full absolute flex items-center z-10 left-16 2xl:left-52 bottom-5 scale-125 max-md:hidden">
            <CarouselPrevious />
          </div>
        }
        {!isEnd &&
          <div className="h-full absolute flex items-center z-10 right-32 bottom-5 scale-125 max-md:hidden">
            <CarouselNext />
          </div>
        }
        <div className="p-slider-1 z-0">
          <CarouselContent overflowHidden={false}>
            <CarouselItem className="md:basis-[5%] lg:basis-[2%] mx-0 scale-125 max-md:basis-[0%] max-md:invisible">
              {isStart &&
                <CarouselPrevious />
              }
            </CarouselItem>
            {podcasts.map((podcast, index) => (
              <CarouselItem key={index} className="basis-4-5/5 md:basis-2/5 lg:basis-[30%] mx-3">
                <div className="flex gap-4">
                  {podcast.field_media_illustration && (
                    <Link href={podcast.path.alias} className="flex-none">
                      <figure className="relative">
                        <ImageWithFallback
                          className="rounded-lg w-32 h-32 object-cover object-center"
                          src={absoluteUrl(podcast.field_media_illustration.field_media_image.uri.url)}
                          width="128"
                          height="128"
                          alt={podcast.field_media_illustration.field_media_image.resourceIdObjMeta.alt || ''}
                          priority

                        />
                        {podcast.field_fichier_video?.field_media_oembed_video && (
                          <div className="bg-neutral-800 text-white p-1 text-xs leading-none text-center absolute top-2 left-2 rounded-sm opacity-80">
                            VIDEO
                          </div>
                        )}
                      </figure>
                    </Link>
                  )}

                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="space-y-2">
                      <Link href={podcast.path.alias}>
                        <h3 className="font-medium text-foreground-dark line-clamp-2 select-none">
                          {podcast.title}
                        </h3>
                      </Link>

                      {podcast.field_emission?.title && (
                        <p className="text-sm text-foreground-dark line-clamp-1">
                          {podcast.field_emission.title}
                        </p>
                      )}
                    </div>

                    {podcast.field_fichier?.field_google_storage_link && (
                      <div className="flex items-center justify-between pt-2">
                        <Button
                          asChild
                          size="icon"
                          style={{ minWidth: "60px", marginRight: "10px" }}
                          iconLeft={<IconPlayerPlayFilled size="20" />}
                        >
                          <AudioPlayerButton
                            media={{
                              src: podcast.field_fichier.field_google_storage_link,
                              metadatas: {
                                illustration: absoluteUrl(podcast.field_media_illustration.field_media_image.uri.url),
                                title: podcast.field_emission?.title || '',
                                episode: podcast.title,
                                presenter: podcast.field_animateurs[0]?.title,
                                nid: podcast.drupal_internal__nid,
                              }
                            }}
                          />
                        </Button>
                        <span className="text-sm text-foreground-light uppercase select-none">
                          {formatDate(podcast.created)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CarouselItem>
            ))}
            <CarouselItem className="basis-4-5/5 md:basis-2/5 lg:basis-[30%]">
              <div className="rounded-xl text-white bg-podcast-illustration bg-primary p-[18px] transition-all relative flex flex-col justify-end h-full overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <Link href="/podcasts" className="stretched-link relative z-10">
                  <h3 className="text-[32px]">
                    Découvrir d&apos;autres podcasts
                  </h3>
                </Link>
              </div>
            </CarouselItem>
            <CarouselItem className="md:basis-[5%] lg:basis-[2%] mx-0 scale-125 max-md:basis-[0%] max-md:invisible">
              {isEnd &&
                <CarouselNext />
              }
            </CarouselItem>
          </CarouselContent>
          <SliderIndicator isStart={isStart} isEnd={isEnd} />
        </div>
      </div>
    </Carousel>
  );
}